import { FC, Fragment } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
// Components
import { ColorPicker } from 'components/ColorPicker';
import { ScoringPlace, VoteResults } from 'components/ResultItems';
// Utilities
import { isRequired } from 'utilities/validation';
// i18next
import { useTranslation } from 'react-i18next';
import QuizTypes from 'types/QuizTypes';

type Props = {
  quizType: QuizTypes;
}

const ResultsFormTheme:FC<Props> = ({ quizType }) => {
  const { t } = useTranslation();

  const isPollQuizzes = [QuizTypes.Poll, QuizTypes.RankingPoll].includes(quizType);
  const isTeamSelectorQuiz = quizType === QuizTypes.TeamSelector;

  const { control, formState: { errors }, watch } = useFormContext();

  const placingGradientStartWatcher = watch('resultsPage.placingGradientStart');
  const placingGradientEndWatcher = watch('resultsPage.placingGradientEnd');
  const placingFontColorWatcher = watch('resultsPage.placingFontColor');
  const currentPlacingGradientStartWatcher = watch('resultsPage.currentPlacingGradientStart');
  const currentPlacingGradientEndWatcher = watch('resultsPage.currentPlacingGradientEnd');
  const currentPlacingFontColorWatcher = watch('resultsPage.currentPlacingFontColor');

  if (isTeamSelectorQuiz) return (
    <div className="mb-4">
      <Controller
        control={control} name="resultsPage.primaryColor" rules={{ required: isRequired }}
        render={({ field: { value, onChange } }) => (
          <ColorPicker
            label={t('labels.primaryColor')}
            value={value}
            required={true}
            error={Boolean(errors.resultsPage?.primaryColor)}
            errorText={errors.resultsPage?.primaryColor ? errors.resultsPage.primaryColor.message : ''}
            onChange={onChange}
            helperText={t('pages.quizForm.publicLeaderboard.teamSelectorPrimaryColorHint')}
          />
        )}
      />
    </div>
  );

  return (
    <Fragment> 
      <h2 className="text-xl font-bold text-white mb-2">
        {isPollQuizzes ? t('labels.participantsResultsGradientColor') : t('labels.participantsPlacingGradientColor')}
      </h2>
      <div className="flex flex-col items-center gap-4 md:flex-row">
        <div className="mb-4">
          <Controller
            control={control} name="resultsPage.placingGradientStart" rules={{ required: isRequired }}
            render={({ field: { value, onChange } }) => (
              <ColorPicker
                label={t('labels.startColor')}
                value={value}
                required={true}
                error={Boolean(errors.resultsPage?.placingGradientStart)}
                errorText={errors.resultsPage?.placingGradientStart ? errors.resultsPage.placingGradientStart.message : ''}
                onChange={onChange}
              />
            )}
          />
        </div>
        <div className="mb-4">
          <Controller
            control={control} name="resultsPage.placingGradientEnd" rules={{ required: isRequired }}
            render={({ field: { value, onChange } }) => (
              <ColorPicker
                label={t('labels.endColor')}
                value={value}
                required={true}
                error={Boolean(errors.resultsPage?.placingGradientEnd)}
                errorText={errors.resultsPage?.placingGradientEnd ? errors.resultsPage.placingGradientEnd.message : ''}
                onChange={onChange}
              />
            )}
          />
        </div>
        <div className="mb-4">
          <Controller
            control={control} name="resultsPage.placingFontColor" rules={{ required: isRequired }}
            render={({ field: { value, onChange } }) => (
              <ColorPicker
                label={t('labels.fontColor')}
                value={value}
                required={true}
                error={Boolean(errors.resultsPage?.placingFontColor)}
                errorText={errors.resultsPage?.placingFontColor ? errors.resultsPage.placingFontColor.message : ''}
                onChange={onChange}
              />
            )}
          />
        </div>
        {isPollQuizzes ? (
          <VoteResults gradientStart={placingGradientStartWatcher} gradientEnd={placingGradientEndWatcher} fontColor={placingFontColorWatcher} />
        ) : (
          <ScoringPlace gradientStart={placingGradientStartWatcher} gradientEnd={placingGradientEndWatcher} fontColor={placingFontColorWatcher} />
        )}
      </div>
      <h2 className="text-xl font-bold text-white mb-2">
        {isPollQuizzes ? t('labels.participantOwnResultGradientColor') : t('labels.participantOwnPlacingGradientColor')}
      </h2>
      <div className="flex flex-col items-center gap-4 md:flex-row">
        <div className="mb-4">
          <Controller
            control={control} name="resultsPage.currentPlacingGradientStart" rules={{ required: isRequired }}
            render={({ field: { value, onChange } }) => (
              <ColorPicker
                label={t('labels.startColor')}
                value={value}
                required={true}
                error={Boolean(errors.resultsPage?.currentPlacingGradientStart)}
                errorText={errors.resultsPage?.currentPlacingGradientStart ? errors.resultsPage.currentPlacingGradientStart.message : ''}
                onChange={onChange}
              />
            )}
          />
        </div>
        <div className="mb-4">
          <Controller
            control={control} name="resultsPage.currentPlacingGradientEnd" rules={{ required: isRequired }}
            render={({ field: { value, onChange } }) => (
              <ColorPicker
                label={t('labels.endColor')}
                value={value}
                required={true}
                error={Boolean(errors.resultsPage?.currentPlacingGradientEnd)}
                errorText={errors.resultsPage?.currentPlacingGradientEnd ? errors.resultsPage.currentPlacingGradientEnd.message : ''}
                onChange={onChange}
              />
            )}
          />
        </div>
        <div className="mb-4">
          <Controller
            control={control} name="resultsPage.currentPlacingFontColor" rules={{ required: isRequired }}
            render={({ field: { value, onChange } }) => (
              <ColorPicker
                label={t('labels.fontColor')}
                value={value}
                required={true}
                error={Boolean(errors.resultsPage?.currentPlacingFontColor)}
                errorText={errors.resultsPage?.currentPlacingFontColor ? errors.resultsPage.currentPlacingFontColor.message : ''}
                onChange={onChange}
              />
            )}
          />
        </div>
        {isPollQuizzes ? (
          <VoteResults gradientStart={currentPlacingGradientStartWatcher} gradientEnd={currentPlacingGradientEndWatcher} fontColor={currentPlacingFontColorWatcher} />
        ) : (
          <ScoringPlace gradientStart={currentPlacingGradientStartWatcher} gradientEnd={currentPlacingGradientEndWatcher} fontColor={currentPlacingFontColorWatcher} />
        )}
      </div>
    </Fragment>
  )
}

export default ResultsFormTheme;
